import React, { useRef, useState } from "react";
import ImageEditor from "./ImageEditor";

export default function ImagePreview({
  text,
  user,
  setText,
  images = [],
  videos = [],
  setImages,
  setVideos,
  setShowPrev,
  setError,
  autoOpen,
  onPickerOpened,
  hideTextInput = false,
}) {
  const mediaInputRef = useRef(null);
  const textRef = useRef(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [detectedUrls, setDetectedUrls] = useState([]);

  const handleMedia = (e) => {
    const files = Array.from(e.target.files);

    files.forEach((file) => {
      const t = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      const isImage =
        /image\/(jpeg|jpg|png|webp|gif|avif)/.test(t) ||
        /\.(jpe?g|png|webp|gif|avif)$/.test(name);
      const isVideo =
        /video\/(mp4|webm|ogg)/.test(t) ||
        /\.(mp4|webm|ogg)$/.test(name);

      if (isImage && file.size > 5 * 1024 * 1024) {
        setError(`${file.name} size is too large! Max 5 MB allowed for images.`);
        return;
      }
      if (isVideo && file.size > 1024 * 1024 * 1024) {
        setError(`${file.name} size is too large! Max 1 GB allowed for videos.`);
        return;
      }

      if (!isImage && !isVideo) {
        setError(
          `${file.name} format is unsupported! Only JPEG, PNG, WebP, GIF, AVIF, MP4, WebM, and OGG are allowed.`
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        if (isImage) {
          setImages((prev) => [...prev, readerEvent.target.result]);
        } else if (isVideo) {
          setVideos((prev) => [...prev, readerEvent.target.result]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageEditorSave = (editedImages) => {
    setImages(editedImages);
  };

  const handleImageEditorClose = () => {
    setShowImageEditor(false);
  };

  // Detect URLs in text
  React.useEffect(() => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = [];
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      urls.push({
        url: match[0],
        index: match.index,
        length: match[0].length
      });
    }
    setDetectedUrls(urls);
  }, [text]);

  const handleTextareaClick = (e) => {
    // Double click to open URL
    if (e.detail === 2) {
      const textarea = textRef.current;
      if (!textarea) return;

      const clickPosition = textarea.selectionStart;
      
      // Check if double-click is on a URL
      for (const urlData of detectedUrls) {
        if (clickPosition >= urlData.index && clickPosition <= urlData.index + urlData.length) {
          e.preventDefault();
          // Open URL in new tab
          window.open(urlData.url, '_blank', 'noopener,noreferrer');
          return;
        }
      }
    }
  };

  const handleTextareaKeyDown = (e) => {
    // Ctrl/Cmd + Click or Enter on selected URL
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      const textarea = textRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = text.substring(start, end);
      
      // Check if selected text is a URL
      const urlRegex = /^https?:\/\/.+/;
      if (urlRegex.test(selectedText)) {
        e.preventDefault();
        window.open(selectedText, '_blank', 'noopener,noreferrer');
      } else {
        // Check if cursor is on a URL
        for (const urlData of detectedUrls) {
          if (start >= urlData.index && start <= urlData.index + urlData.length) {
            e.preventDefault();
            window.open(urlData.url, '_blank', 'noopener,noreferrer');
            return;
          }
        }
      }
    }
  };

  const handleUrlClick = (url, e) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Auto-open the hidden input when requested
  React.useEffect(() => {
    if (autoOpen && mediaInputRef.current) {
      mediaInputRef.current.value = '';
      mediaInputRef.current.click();
      if (onPickerOpened) onPickerOpened();
    }
  }, [autoOpen, onPickerOpened]);

  return (
    <div className="overflow_a scrollbar">
      {showImageEditor && (
        <ImageEditor
          images={images}
          onClose={handleImageEditorClose}
          onSave={handleImageEditorSave}
        />
      )}
      {/* Description input for posts with media */}
      {(images.length > 0 || videos.length > 0) && !hideTextInput && (
        <div className="image_preview_text_input">
          <div className="image_preview_textarea_wrap">
            <textarea
              ref={textRef}
              maxLength="500"
              value={text}
              placeholder={`What's on your mind, ${user?.first_name || 'Safida'}?`}
              className="post_input input2"
              onChange={(e) => setText(e.target.value)}
              onDoubleClick={handleTextareaClick}
              onKeyDown={handleTextareaKeyDown}
              rows={2}
            ></textarea>
          </div>
          {/* Show detected URLs as clickable links */}
          {detectedUrls.length > 0 && (
            <div className="detected_urls_preview">
              <div className="detected_urls_label">Detected Links:</div>
              {detectedUrls.map((urlData, index) => (
                <a
                  key={index}
                  href={urlData.url}
                  className="detected_url_link"
                  onClick={(e) => handleUrlClick(urlData.url, e)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {urlData.url}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="add_pics_wrap">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif,video/mp4,video/webm,video/ogg"
          multiple
          hidden
          ref={mediaInputRef}
          onChange={handleMedia}
        />
        {(images.length || videos.length) ? (
          <div className="add_pics_inside1 p0">
            <div className="preview_actions">
              {images.length > 0 && (
                <button
                  className="hover1"
                  onClick={() => {
                    setShowImageEditor(true);
                  }}
                  style={{ marginRight: '10px' }}
                >
                  <i className="edit_icon"></i>
                  Edit photos
                </button>
              )}
              <button
                className="hover1"
                onClick={() => {
                  mediaInputRef.current.click();
                }}
              >
                <i className="addPhoto_icon"></i>
                Add Photos/Videos
              </button>
            </div>
            <div
              className="small_white_circle"
              onClick={() => {
                setImages([]);
                setVideos([]);
                // Clear the file input to allow selecting the same file again
                if (mediaInputRef.current) {
                  mediaInputRef.current.value = '';
                }
              }}
            >
              <i className="exit_icon"></i>
            </div>
            <div
              className={
                images.length + videos.length === 1
                  ? "preview1"
                  : (images.length + videos.length) % 2 === 0
                  ? "preview6"
                  : "preview6 singular_grid"
              }
            >
              {/* Image Preview */}
              {images.map((img, i) => (
                <img
                  src={img}
                  key={`image-${i}`}
                  alt="Preview"
                  className="preview_image"
                />
              ))}

              {/* Video Preview */}
              {videos.map((video, i) => (
                <div key={`video-${i}`} className="video_preview">
                  <video 
                    controls 
                    className="preview_video"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#000'
                    }}
                  >
                    <source src={video} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="add_pics_inside1">
            <div
              className="small_white_circle"
              onClick={() => {
                setShowPrev(false);
                // Clear the file input when exiting preview mode
                if (mediaInputRef.current) {
                  mediaInputRef.current.value = '';
                }
              }}
            >
              <i className="exit_icon"></i>
            </div>
            <div
              className="add_col"
              onClick={() => {
                mediaInputRef.current.click();
              }}
            >
              <div className="add_circle">
                <i className="addPhoto_icon"></i>
              </div>
              <span>Add Photos/Videos</span>
              <span>or drag and drop</span>
            </div>
          </div>
        )}
        {/* <div className="add_pics_inside2">
          <div className="add_circle">
            <i className="phone_icon"></i>
          </div>
          <div className="mobile_text">Add photos or videos from your mobile device.</div>
          <span className="addphone_btn">Add</span>
        </div> */}
      </div>
    </div>
  );
}

